import { Component, ChangeDetectorRef } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/session.service";
import { DashboardService } from "../../dashboard.service";
import { GenericWidgetComponent } from "../generic-widget/generic-widget.component";
import { Widget } from "../widgets";
import { PermissionService } from "src/app/permission.service";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_TRIPIST_KEY,
} from "src/app/features.constants";
import { IntegrationsService } from "src/app/integrations/integrations.service";
import { take } from "rxjs/operators";
import { forkJoin } from "rxjs";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

@Component({
  selector: "app-assistant-shares-ratings-feedback-widget",
  templateUrl: "./assistant-shares-ratings-feedback-widget.component.html",
  styleUrls: ["./assistant-shares-ratings-feedback-widget.component.scss"],
})
export class AssistantSharesRatingsFeedbackWidgetComponent extends GenericWidgetComponent {
  widget!: Widget;
  sharesRatingsFeedbackChartOptions: any;
  totalSharesAllTime: any;
  threadsWithRatingsAllTime: any;
  averageRatingAllTime: any;
  threadsWithFeedbackAllTime: any;
  sharesRatingsFeedbackData: any;
  enabledIntegrationTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
  primaryIntegrationType: 'tripist' | 'explorist' | 'aura' | null = null;
  integrationIds: number[] = [];
  noIntegrationFound: boolean = false;
  bucket: "day" | "week" | "month" | "quarter" | "year" = "day";

  constructor(
    private DashboardService: DashboardService,
    private SessionService: SessionService,
    private PermissionService: PermissionService,
    private IntegrationsService: IntegrationsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  get accountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get accountAppID() {
    return this.SessionService.currentAppValue!.id;
  }

  async ngOnInit(): Promise<void> {
    await this.determineIntegrationType();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // Don't call initData here - wait for integration detection to complete
    // this.initData();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        if (
          this.filterForm.valid &&
          this.filterForm.controls.startDate.value <
            this.filterForm.controls.endDate.value
        ) {
          this.initData();
        }
      });
  }

  private async determineIntegrationType(): Promise<void> {
    const enabledTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
    if (this.PermissionService.hasFeature(FEATURE_TRIPIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('tripist');
    }
    if (this.PermissionService.hasFeature(FEATURE_EXPLORIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('explorist');
    }
    if (this.PermissionService.hasFeature(FEATURE_AURA_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('aura');
    }

    this.enabledIntegrationTypes = enabledTypes;

    const integrations = await this.getIntegrations();
    const providerByType: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'tripist_assistant',
      explorist: 'explorist_assistant',
      aura: 'aura_assistant',
    };
    const preferredType = this.widget?.widgetDTO?.assistantType;
    const orderedTypes = (
      preferredType && enabledTypes.includes(preferredType)
        ? [preferredType, ...enabledTypes.filter((type) => type !== preferredType)]
        : enabledTypes
    ) as Array<'tripist' | 'explorist' | 'aura'>;

    let selectedType: 'tripist' | 'explorist' | 'aura' | null = null;
    let selectedId: number | null = null;
    for (const type of orderedTypes) {
      const integration = integrations.find((item: any) => item.provider?.type === providerByType[type]);
      if (integration?.id) {
        selectedType = type;
        selectedId = integration.id;
        break;
      }
    }

    this.primaryIntegrationType = selectedType ?? ((preferredType as any) || null);
    this.integrationIds = selectedId ? [selectedId] : [];
    this.noIntegrationFound = !selectedId;
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Now that integration detection is complete, initialize data
    this.initData();
  }

  private async getIntegrations(): Promise<any[]> {
    try {
      const accountId = this.SessionService.currentAccountValue?.accountID;
      const appId = this.SessionService.currentAppValue?.id;
      
      if (!accountId || !appId) {
        console.warn('Missing session data for integration lookup');
        return [];
      }
      
      const response: any = await this.IntegrationsService.getIntegrations(
        accountId,
        appId,
        true
      ).pipe(take(1)).toPromise();
      
      return Array.isArray(response) ? response : response?.records || [];
    } catch (error) {
      console.error(`Error fetching integrations for assistant widgets:`, error);
      return [];
    }
  }

  get widgetTitle(): string {
    return `${this.primaryAssistantLabel} Shares/Ratings/Feedback`;
  }

  get enabledAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.enabledIntegrationTypes.map((type) => labels[type]).join(', ') || 'Assistant';
  }

  get primaryAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.primaryIntegrationType ? labels[this.primaryIntegrationType] : 'Assistant';
  }

  initData() {
    if (this.noIntegrationFound) {
      console.warn('No integration found for Assistant Shares/Ratings/Feedback widget');
      return;
    }

    if (this.integrationIds.length === 0) {
      console.warn('No integration IDs available for Assistant Shares/Ratings/Feedback widget');
      return;
    }

    this.filterForm.controls.endDate.value.setHours(23, 59, 59);

    const requests = this.integrationIds.map((integrationId) =>
      this.DashboardService.getAssistantSharesRatingsFeedback(
        this.accountID,
        this.accountAppID,
        integrationId,
        this.filterForm.controls.startDate.value,
        this.filterForm.controls.endDate.value,
      )
    );

    this.elementLoader
      .waitForEvent(forkJoin(requests))
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((responses) => {
        this.sharesRatingsFeedbackData = this.mergeSharesRatingsFeedbackResponses(responses as any[]);
        this.bucket = this.sharesRatingsFeedbackData.bucket || "day";
        
        if (this.sharesRatingsFeedbackData && this.sharesRatingsFeedbackData.data && this.sharesRatingsFeedbackData.data.length > 0) {
          // Get the all-time totals from the first entry (they're the same in all entries)
          const firstEntry = this.sharesRatingsFeedbackData.data[0];
          this.totalSharesAllTime = parseInt(firstEntry.total_shares_all_time) || 0;
          this.threadsWithRatingsAllTime = parseInt(firstEntry.threads_with_ratings_all_time) || 0;
          this.averageRatingAllTime = parseFloat(firstEntry.average_rating_all_time) || 0;
          this.threadsWithFeedbackAllTime = parseInt(firstEntry.threads_with_feedback_all_time) || 0;
        } else {
          this.totalSharesAllTime = 0;
          this.threadsWithRatingsAllTime = 0;
          this.averageRatingAllTime = 0;
          this.threadsWithFeedbackAllTime = 0;
        }

        this.initSharesRatingsFeedbackChart();
      });
  }

  private mergeSharesRatingsFeedbackResponses(responses: any[]): any {
    const byDate = new Map<string, any>();
    const bucket = (responses && responses[0] && responses[0].bucket) || "day";
    let totalSharesAllTime = 0;
    let totalRatingsAllTime = 0;
    let weightedRatingAllTimeSum = 0;
    let totalFeedbackAllTime = 0;

    for (const response of responses || []) {
      const rows = response?.data || [];
      if (rows.length > 0) {
        const first = rows[0];
        const ratingsAllTime = Number(first.threads_with_ratings_all_time || 0);
        const avgAllTime = Number(first.average_rating_all_time || 0);
        totalSharesAllTime += Number(first.total_shares_all_time || 0);
        totalRatingsAllTime += ratingsAllTime;
        weightedRatingAllTimeSum += avgAllTime * ratingsAllTime;
        totalFeedbackAllTime += Number(first.threads_with_feedback_all_time || 0);
      }

      for (const row of rows) {
        const dateKey = row.date;
        if (!byDate.has(dateKey)) {
          byDate.set(dateKey, {
            date: dateKey,
            daily_threads_with_shares: 0,
            daily_threads_with_ratings: 0,
            daily_threads_with_feedback: 0,
            _weightedDailyRatingSum: 0,
          });
        }

        const target = byDate.get(dateKey);
        const dailyRatings = Number(row.daily_threads_with_ratings || 0);
        const dailyAverageRating = Number(row.daily_average_rating || 0);
        target.daily_threads_with_shares += Number(row.daily_threads_with_shares || 0);
        target.daily_threads_with_ratings += dailyRatings;
        target.daily_threads_with_feedback += Number(row.daily_threads_with_feedback || 0);
        target._weightedDailyRatingSum += dailyAverageRating * dailyRatings;
      }
    }

    const data = Array.from(byDate.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((row: any) => ({
        date: row.date,
        daily_threads_with_shares: row.daily_threads_with_shares,
        daily_threads_with_ratings: row.daily_threads_with_ratings,
        daily_average_rating: row.daily_threads_with_ratings > 0
          ? row._weightedDailyRatingSum / row.daily_threads_with_ratings
          : 0,
        daily_threads_with_feedback: row.daily_threads_with_feedback,
        total_shares_all_time: totalSharesAllTime,
        threads_with_ratings_all_time: totalRatingsAllTime,
        average_rating_all_time: totalRatingsAllTime > 0 ? weightedRatingAllTimeSum / totalRatingsAllTime : 0,
        threads_with_feedback_all_time: totalFeedbackAllTime,
      }));

    return { data, bucket };
  }

  initSharesRatingsFeedbackChart() {
    // Check if we have the expected data structure
    if (!this.sharesRatingsFeedbackData || !this.sharesRatingsFeedbackData.data || !Array.isArray(this.sharesRatingsFeedbackData.data)) {
      console.warn('Assistant Shares/Ratings/Feedback Widget - Missing expected data structure:', this.sharesRatingsFeedbackData);
      return;
    }
    
    const type = this.bucket;

    const chartDataObj: {
      type: "day" | "week" | "month" | "quarter" | "year";
      data: {
        date: Date;
        shares: number;
        ratings: number;
        averageRating: number;
        feedback: number;
      }[];
    } = { type, data: [] };

    if (type === "day" || type === "week" || type === "month" || type === "quarter" || type === "year") {
      for (let i = 0; i < this.sharesRatingsFeedbackData.data.length; i++) {
        const dayData = this.sharesRatingsFeedbackData.data[i];
        
        chartDataObj.data.push({
          date: new Date(dayData.date),
          shares: parseInt(dayData.daily_threads_with_shares) || 0,
          ratings: parseInt(dayData.daily_threads_with_ratings) || 0,
          averageRating: parseFloat(dayData.daily_average_rating) || 0,
          feedback: parseInt(dayData.daily_threads_with_feedback) || 0,
        });
      }
    }

    // Ensure all dates are Date objects
    chartDataObj.data = chartDataObj.data.map(item => ({
      ...item,
      date: item.date instanceof Date ? item.date : new Date(item.date)
    }));

    this.sharesRatingsFeedbackChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 30,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: chartDataObj.data.map((data: any) => data.date.toISOString()),
        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            // Ensure d is a Date object
            const date = d instanceof Date ? d : new Date(d);
            return this.getXLabel(date, type as any);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisPointer: {
          type: 'shadow'
        }
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          // Ensure the date is a Date object
          const date = params[0].axisValue instanceof Date ? params[0].axisValue : new Date(params[0].axisValue);
          return this.getTooltip(params, type as any, date);
        },
      },
      series: [
        {
          name: 'Shares',
          data: chartDataObj.data.map((data: any) => data.shares),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "#7dc4fd" },
          lineStyle: { color: "#7dc4fd" },
        },
        {
          name: 'Ratings',
          data: chartDataObj.data.map((data: any) => data.ratings),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "#FFD700" },
          lineStyle: { color: "#FFD700" },
        },
        {
          name: 'Feedback',
          data: chartDataObj.data.map((data: any) => data.feedback),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "#4CAF50" },
          lineStyle: { color: "#4CAF50" },
        },
        {
          name: 'Average Rating',
          data: chartDataObj.data.map((data: any) => data.averageRating),
          showSymbol: false,
          type: "line",
          // Hide this series from the chart but keep it in tooltip
          silent: true,
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
        }
      ],
    };
  }

  private getQuarter(date: Date): number {
    return Math.floor(date.getMonth() / 3) + 1;
  }

  private getXLabel(date: Date, type: "day" | "week" | "month" | "quarter" | "year"): string {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (type === "day") {
      return this.getDayLabel(dateObj);
    } else if (type === "week") {
      return `${MONTHS[dateObj.getMonth()]} ${dateObj.getDate()}`;
    } else if (type === "month") {
      return MONTHS[dateObj.getMonth()];
    } else if (type === "quarter") {
      return `Q${this.getQuarter(dateObj)} ${dateObj.getFullYear()}`;
    }
    return `${dateObj.getFullYear()}`;
  }

  private getDayLabel(date: Date) {
    // Check if we have more than 30 days of data
    if (this.sharesRatingsFeedbackData && this.sharesRatingsFeedbackData.data && this.sharesRatingsFeedbackData.data.length > 30) {
      // Show every other day for better readability
      const day = date.getDate();
      return day % 2 === 0 ? "" : day.toString();
    }
    return date.getDate().toString();
  }

  private getTooltip(params: any, type: "day" | "week" | "month" | "quarter" | "year", date: Date): string {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    
    let dateStr = "";
    if (type === "day") {
      dateStr = `${MONTHS[dateObj.getMonth()]} ${dateObj.getDate()} ${dateObj.getFullYear()}`;
    } else if (type === "week") {
      dateStr = `Week of ${MONTHS_FULL[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
    } else if (type === "month") {
      dateStr = `${MONTHS_FULL[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
    } else if (type === "quarter") {
      dateStr = `Q${this.getQuarter(dateObj)} ${dateObj.getFullYear()}`;
    } else {
      dateStr = `${dateObj.getFullYear()}`;
    }

    let tooltip = `<div style="color: black;">${dateStr}<br/>`;
    
    params.forEach((param: any) => {
      const color = param.color;
      const name = param.seriesName;
      const value = param.value;
      
      if (name === 'Average Rating') {
        tooltip += `<span style="color: ${color};"><strong>${name}: ${value.toFixed(2)}</strong></span><br/>`;
      } else {
        tooltip += `<span style="color: ${color};"><strong>${name}: ${value}</strong></span><br/>`;
      }
    });
    
    tooltip += "</div>";
    return tooltip;
  }
}
